import {
  type CreateUserBody,
  type ImpersonateStartResponse,
  type IssueManualLicenseBody,
  impersonateStartResponse,
  type TransferLicenseBody,
  type UserAccessMutationResponse,
  type UserDetail,
  type UserListItem,
  userAccessMutationResponseDto,
  userDetailDto,
  userListItemDto,
} from '@planuze/admin-contracts';
import { z } from 'zod';
import { ApiError, HTTP, parseJson, readErrorCode, request } from './http.js';

/* Cliente REST de usuários (28-12, ADR-0356/0348). Consome o admin-service:
   list/detail (auth+licensing agregados), impersonate, license-ops, device cooldown,
   session revoke. */

export interface UsersQuery {
  readonly search?: string | undefined;
  readonly cursor?: string | undefined;
  /* Filtro server-side de verificação (ADR-0348): `verified`/`unverified`; ausente = todos. */
  readonly verified?: 'verified' | 'unverified' | undefined;
}

const usersPageSchema = z.object({
  items: z.array(userListItemDto),
  nextCursor: z.string().nullish(),
});

const HTTP_NOT_FOUND = 404;
const HTTP_BAD_REQUEST = 400;
const HTTP_CONFLICT = 409;

async function userError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  switch (res.status) {
    case HTTP_NOT_FOUND:
      return new ApiError(res.status, 'not_found', 'user resource not found');
    case HTTP_BAD_REQUEST:
      return new ApiError(res.status, 'not_blocked', code || 'invalid request');
    case HTTP_CONFLICT:
      return new ApiError(res.status, 'grace_expired', code || 'conflict');
    default:
      return new ApiError(res.status, 'server_error', code || 'request failed');
  }
}

function buildQuery(query: UsersQuery): string {
  const params = new URLSearchParams();
  if (query.search !== undefined && query.search.length > 0) params.set('search', query.search);
  if (query.cursor !== undefined && query.cursor.length > 0) params.set('cursor', query.cursor);
  if (query.verified !== undefined) params.set('verified', query.verified);
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export async function listUsers(
  query: UsersQuery,
): Promise<{ readonly items: readonly UserListItem[]; readonly nextCursor: string | null }> {
  const res = await request(`/v1/admin/users${buildQuery(query)}`);
  if (!res.ok) throw await userError(res);
  const page = await parseJson(res, usersPageSchema);
  return { items: page.items, nextCursor: page.nextCursor ?? null };
}

export async function getUser(id: string): Promise<UserDetail> {
  const res = await request(`/v1/admin/users/${encodeURIComponent(id)}`);
  if (!res.ok) throw await userError(res);
  return parseJson(res, userDetailDto);
}

/* Erros da criação de conta comum (ADR-0710): 409 email_taken (o servidor é a
   autoridade — colisão case-insensitive); 401/403 sem permissão. Demais →
   server_error. O serverCode preserva o código cru p/ o error-mapper pt-BR. */
async function createUserError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  if (res.status === HTTP.conflict && code === 'email_taken') {
    return new ApiError(res.status, 'email_taken', code, code);
  }
  if (res.status === HTTP.unauthorized || res.status === HTTP.forbidden) {
    return new ApiError(res.status, 'unauthorized', code || 'forbidden', code);
  }
  return new ApiError(res.status, 'server_error', code || 'create failed', code);
}

/** POST /v1/admin/users — cria uma conta comum pré-verificada (sem senha; login por OTP). */
export async function createUser(body: CreateUserBody): Promise<UserListItem> {
  const res = await request('/v1/admin/users', { method: 'POST', body });
  if (!res.ok) throw await createUserError(res);
  return parseJson(res, userListItemDto);
}

const enc = (s: string): string => encodeURIComponent(s);

export async function impersonate(id: string, reason: string): Promise<ImpersonateStartResponse> {
  const res = await request(`/v1/admin/users/${enc(id)}/impersonate`, {
    method: 'POST',
    body: { reason },
  });
  if (!res.ok) throw await userError(res);
  return parseJson(res, impersonateStartResponse);
}

async function changeUserAccess(
  id: string,
  action: 'block' | 'unblock',
  reason: string,
): Promise<UserAccessMutationResponse> {
  const res = await request(`/v1/admin/users/${enc(id)}/${action}`, {
    method: 'POST',
    body: { reason },
  });
  if (!res.ok) throw await userError(res);
  return parseJson(res, userAccessMutationResponseDto);
}

export const blockUser = (id: string, reason: string): Promise<UserAccessMutationResponse> =>
  changeUserAccess(id, 'block', reason);

export const unblockUser = (id: string, reason: string): Promise<UserAccessMutationResponse> =>
  changeUserAccess(id, 'unblock', reason);

export async function revokeLicense(userId: string, packId: string, reason: string): Promise<void> {
  const res = await request(`/v1/admin/users/${enc(userId)}/licenses/${enc(packId)}/revoke`, {
    method: 'POST',
    body: { reason },
  });
  if (!res.ok) throw await userError(res);
}

export async function transferLicense(userId: string, body: TransferLicenseBody): Promise<void> {
  const res = await request(`/v1/admin/users/${enc(userId)}/licenses/transfer`, {
    method: 'POST',
    body,
  });
  if (!res.ok) throw await userError(res);
}

export async function issueManualLicense(
  userId: string,
  body: IssueManualLicenseBody,
): Promise<void> {
  const res = await request(`/v1/admin/users/${enc(userId)}/licenses/issue-manual`, {
    method: 'POST',
    body,
  });
  if (!res.ok) throw await userError(res);
}

export async function cooldownDevice(
  deviceId: string,
  durationHours: number,
  reason: string,
): Promise<void> {
  const res = await request(`/v1/admin/devices/${enc(deviceId)}/cooldown`, {
    method: 'POST',
    body: { durationHours, reason },
  });
  if (!res.ok) throw await userError(res);
}

export async function revokeSession(
  userId: string,
  sessionId: string,
  reason: string,
): Promise<void> {
  const res = await request(`/v1/admin/users/${enc(userId)}/sessions/${enc(sessionId)}/revoke`, {
    method: 'POST',
    body: { reason },
  });
  if (!res.ok) throw await userError(res);
}
